import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Container, VStack, HStack, Box, Heading, Text, Divider, Button, Input, Checkbox,
    FormControl, FormLabel, SimpleGrid, Card, CardBody, Badge, Alert, AlertIcon,
    useToast, Stepper, Step, StepIndicator, StepStatus, StepIcon, StepNumber,
    StepTitle, StepSeparator, useSteps, Image, Flex,
    Table, Thead, Tbody, Tr, Th, Td,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import StreetAutocompleteInput from '../Components/SiteLanding/StreetAutocompleteInput';
import { track } from '../utils/analytics';

// ── Shared SLB brand chrome (matches ProductWebsite.jsx) ──────────────────
const SlbNavbar = () => (
    <Box as="nav" position="sticky" top={0} zIndex={100} bg="white" borderBottom="1px solid" borderColor="gray.100" boxShadow="sm">
        <Container maxW="1200px">
            <Flex py={3} align="center" justify="space-between">
                <Box as="a" href="/slb">
                    <Image src="/slb-logo.png" alt="Smart Laundry Basket" h={{ base: '38px', md: '48px' }} objectFit="contain" />
                </Box>
                <Button as="a" href="/slb" size="sm" variant="ghost" colorScheme="blue">
                    ← Back to home
                </Button>
            </Flex>
        </Container>
    </Box>
);

const SlbFooter = () => (
    <Box py={8} bg="gray.900" color="gray.400" mt={12}>
        <Container maxW="1200px">
            <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align="center" gap={4}>
                <HStack spacing={3}>
                    <Box bg="white" borderRadius="lg" px={2.5} py={1.5}>
                        <Image src="/slb-logo.png" alt="Smart Laundry Basket" h="34px" objectFit="contain" />
                    </Box>
                    <Text fontSize="sm">Open source platform for service businesses.</Text>
                </HStack>
                <Text fontSize="xs">&copy; {new Date().getFullYear()} Smart Laundry Basket. All rights reserved.</Text>
            </Flex>
        </Container>
    </Box>
);

/**
 * QuickStartPage — express tenant onboarding for trade shows / on-the-spot signups.
 *
 * Deliberately minimal: Business Info → Branding → Agreement → Review. Services,
 * schedule, payments, add-ons, and API keys are all SKIPPED and left to the
 * backend defaults (see /api/platform/onboard): a full-week 8-5 schedule and a
 * starter service catalog (2 wash & fold, 2 per-bag, 2 comforter sizes) are
 * seeded so the admin site + booking site work immediately. The tenant refines
 * everything later in admin. No email verification — we still collect the email
 * for the welcome/records, but don't gate on it.
 *
 * Reuses the same POST /api/platform/onboard endpoint as the full wizard; it
 * applies safe column defaults for every field we omit.
 */
const THEME_COLORS = [
    { name: 'Blue', value: 'blue', bg: '#3182CE' },
    { name: 'Green', value: 'green', bg: '#38A169' },
    { name: 'Purple', value: 'purple', bg: '#805AD5' },
    { name: 'Teal', value: 'teal', bg: '#319795' },
    { name: 'Orange', value: 'orange', bg: '#DD6B20' },
    { name: 'Red', value: 'red', bg: '#E53E3E' },
    { name: 'Pink', value: 'pink', bg: '#D53F8C' },
    { name: 'Cyan', value: 'cyan', bg: '#00B5D8' },
];

const steps = [
    { title: 'Business' },
    { title: 'Branding' },
    { title: 'Agreement' },
    { title: 'Review' },
];

const AGREEMENT_TERMS = 'Tiered platform fee applies to managed hosting based on monthly revenue: $0-$999 Free, $1k-$5k $49/mo, $5k-$10k $74/mo, $10k-$15k $99/mo, $15k-$25k $124/mo, $25k+ $149/mo cap. Calendar-month billing, payment due within 30 days. Optional usage-based add-ons billed separately when enabled: SMS notifications, AI website chat, AI item counting. Add-ons can be toggled anytime from the WDF POS; charges apply only while enabled and all toggles are logged.';

const QuickStartPage = () => {
    const toast = useToast();
    const { activeStep, setActiveStep } = useSteps({ index: 0, count: steps.length });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    // Business Info (minimal)
    const [biz, setBiz] = useState({
        laundryName: '', street: '', city: '', state: '', zipCode: '', country: 'USA',
        timezone: 'America/Chicago', contactPhone: '', contactEmail: '',
        ownerFirstName: '', ownerLastName: '', ownerPhone: '', ownerEmail: '',
        hideHomeAddress: false,
    });
    const up = (f, v) => setBiz(prev => ({ ...prev, [f]: v }));

    // Opened from a directory "Claim it free" link: prefill from the basic listing.
    const [claim, setClaim] = useState(null);
    useEffect(() => {
        const claimId = new URLSearchParams(window.location.search).get('claim');
        if (!claimId || !/^\d+$/.test(claimId)) return;
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/directory/outside/${claimId}`)
            .then(({ data }) => {
                setClaim({ id: claimId, name: data.name });
                setBiz(prev => ({
                    ...prev,
                    laundryName: prev.laundryName || data.name || '',
                    street: prev.street || data.street || '',
                    city: prev.city || data.city || '',
                    state: prev.state || data.state || '',
                    zipCode: prev.zipCode || data.zipCode || '',
                    contactPhone: prev.contactPhone || data.phone || '',
                }));
            })
            .catch(() => {});
    }, []);

    // Branding
    const [themeColor, setThemeColor] = useState('blue');
    const [logoBase64, setLogoBase64] = useState('');
    const [logoPreview, setLogoPreview] = useState('');
    const [tagline, setTagline] = useState('');

    // Agreement
    const [agreementSigned, setAgreementSigned] = useState(false);
    const [signatureName, setSignatureName] = useState('');
    const [signatureDate] = useState(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return !!(biz.laundryName && biz.ownerPhone && biz.street && biz.city && biz.state && biz.zipCode);
            case 1:
                return true; // branding optional
            case 2:
                return agreementSigned && signatureName.trim().length > 2;
            default:
                return true;
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // Omit services / deliveryTimeSlots / stripe / apiKeys / emailVerificationToken
            // entirely — the backend seeds a full-week schedule + starter catalog
            // and skips verification when no token is present.
            const payload = {
                ...biz,
                themeColor,
                logoBase64: logoBase64 || null,
                tagline,
                serviceableZipCodes: biz.zipCode ? [biz.zipCode] : [],
                claimListingId: claim?.id || null,
                smsEnabled: false,
                multiLocation: 'none',
                agreement: {
                    signed: agreementSigned,
                    signatureName,
                    signatureDate,
                    terms: AGREEMENT_TERMS,
                },
            };
            const res = await axios.post(
                `${process.env.REACT_APP_AWS_API_URL}/api/platform/onboard`,
                payload
            );
            if (res.data.status === 'success') {
                track('operator_signup', { source: claim ? 'directory_claim' : 'quickstart' });
                setResult(res.data);
                toast({ title: 'Your laundry is live!', status: 'success', duration: 5000 });
            } else {
                toast({ title: 'Error', description: res.data.message, status: 'error', duration: 5000 });
            }
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.message || err.message;
            toast({ title: 'Error', description: detail, status: 'error', duration: 5000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Results screen ──────────────────────────────────────────────────────
    if (result) {
        return (
            <Box bg="gray.50" minH="100vh">
                <SlbNavbar />
                <Container maxW="container.md" py={10}>
                <VStack spacing={6} align="stretch">
                    <Box textAlign="center">
                        <Heading size="lg" color="green.600" mb={2}>🎉 You're Live!</Heading>
                        <Text color="gray.600">Save these details. You can log into the WDF POS right now.</Text>
                    </Box>
                    <Alert status="success" borderRadius="md"><AlertIcon />Your laundry has been created and is ready to use.</Alert>
                    <Card>
                        <CardBody>
                            <VStack spacing={3} align="stretch">
                                <Heading size="sm">Shop</Heading>
                                <SimpleGrid columns={2} spacing={2}>
                                    <Text fontWeight="bold">Laundry ID:</Text><Text>{result.laundry?.laundryId}</Text>
                                    <Text fontWeight="bold">Name:</Text><Text>{result.laundry?.laundryName}</Text>
                                    <Text fontWeight="bold">WDF POS URL:</Text><Text color="blue.500">{result.laundry?.adminUrl}</Text>
                                    <Text fontWeight="bold">Customer Site:</Text><Text color="blue.500">{result.laundry?.customerUrl}</Text>
                                    <Text fontWeight="bold">Device Code:</Text><Badge colorScheme="purple" fontSize="md" p={1}>{result.laundry?.deviceRegistrationCode}</Badge>
                                </SimpleGrid>
                            </VStack>
                        </CardBody>
                    </Card>
                    <Card>
                        <CardBody>
                            <VStack spacing={3} align="stretch">
                                <Heading size="sm">Owner Login</Heading>
                                <SimpleGrid columns={2} spacing={2}>
                                    <Text fontWeight="bold">Employee ID:</Text><Badge colorScheme="blue" fontSize="md">{result.owner?.employeeId}</Badge>
                                    <Text fontWeight="bold">Passcode:</Text><Badge colorScheme="orange" fontSize="md">{result.owner?.passcode}</Badge>
                                    <Text fontWeight="bold">Phone (login):</Text><Text>{biz.ownerPhone}</Text>
                                </SimpleGrid>
                            </VStack>
                        </CardBody>
                    </Card>
                    <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        Free to start — from $49/mo after you pass $1,000 in monthly revenue (max $149/mo).
                    </Alert>
                    <HStack spacing={4} justify="center">
                        <Button colorScheme="blue" onClick={() => window.open(`${result.laundry?.adminUrl}`, '_blank')}>Open WDF POS</Button>
                        <Button variant="outline" onClick={() => window.open(`${result.laundry?.customerUrl}`, '_blank')}>View Customer Site</Button>
                    </HStack>
                </VStack>
                </Container>
                <SlbFooter />
            </Box>
        );
    }

    return (
        <Box bg="gray.50" minH="100vh">
            <Helmet>
                <title>Get Started — Smart Laundry Basket</title>
                <meta name="description" content="Set up your laundry business in minutes with Smart Laundry Basket. Free to start, then from $49/mo after $1,000/mo revenue. No setup fees." />
            </Helmet>
            <SlbNavbar />

            {/* Branded gradient hero — mirrors the SLB product site look */}
            <Box position="relative" overflow="hidden" bg="linear-gradient(135deg, #2B6CB0 0%, #3182CE 55%, #4299E1 100%)" color="white" py={{ base: 10, md: 14 }}>
                <Container maxW="container.md" textAlign="center" position="relative" zIndex={1}>
                    <VStack spacing={3}>
                        <Badge bg="whiteAlpha.300" color="white" px={4} py={1} borderRadius="full" fontSize="xs" fontWeight="700">
                            💸 Free under $1,000/mo revenue
                        </Badge>
                        <Heading size={{ base: 'lg', md: 'xl' }} fontWeight="800" textShadow="0 2px 20px rgba(0,0,0,0.25)">
                            Get Set Up in Minutes
                        </Heading>
                        <Text maxW="560px" fontSize={{ base: 'sm', md: 'md' }} fontWeight="500" color="whiteAlpha.900">
                            Just the essentials — you'll walk away with a working WDF POS and a live booking site.
                            Free to start, then from $49/mo after $1,000 in monthly revenue — never more than $149/mo.
                        </Text>
                        <HStack justify="center" mt={1} spacing={{ base: 4, md: 6 }} fontSize="xs" color="whiteAlpha.900" flexWrap="wrap">
                            <Text>✓ Free to start</Text><Text>✓ No setup fees</Text><Text>✓ 2-minute signup</Text>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

        <Container maxW="container.md" py={{ base: 4, md: 8 }} px={{ base: 4, md: 6 }} mt={{ base: -6, md: -8 }}>
            <Card bg="white" borderRadius="2xl" boxShadow="xl" borderWidth="1px" borderColor="gray.100">
              <CardBody p={{ base: 4, md: 8 }}>
            <VStack spacing={{ base: 4, md: 6 }} align="stretch">

                <Stepper index={activeStep} colorScheme="blue" size={{ base: 'sm', md: 'md' }}>
                    {steps.map((step, index) => (
                        <Step key={index} onClick={() => index < activeStep && setActiveStep(index)}>
                            <StepIndicator>
                                <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                            </StepIndicator>
                            <Box flexShrink="0" display={{ base: 'none', md: 'block' }}>
                                <StepTitle>{step.title}</StepTitle>
                            </Box>
                            <StepSeparator />
                        </Step>
                    ))}
                </Stepper>

                <Divider />

                {claim && (
                    <Alert status="success" borderRadius="lg">
                        <AlertIcon />
                        You're claiming the directory listing for {claim.name}. We filled in what we had. Check it and finish
                        signing up, and your shop goes live in the directory with online ordering.
                    </Alert>
                )}

                {/* Step 1: Business Info */}
                {activeStep === 0 && (
                    <VStack spacing={4} align="stretch">
                        <Heading size="md">Business Information</Heading>
                        <FormControl isRequired>
                            <FormLabel>Laundry Name</FormLabel>
                            <Input placeholder="e.g. Fresh & Clean Laundry" value={biz.laundryName} onChange={e => up('laundryName', e.target.value)} />
                        </FormControl>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Street Address</FormLabel>
                                <StreetAutocompleteInput
                                    street={biz.street}
                                    onStreetChange={(v) => up('street', v)}
                                    onSelect={({ street, city, state, zip }) => {
                                        if (street) up('street', street);
                                        if (city) up('city', city);
                                        if (state) up('state', state);
                                        if (zip) up('zipCode', zip);
                                    }}
                                    placeholder="Start typing your address"
                                />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>City</FormLabel>
                                <Input placeholder="Austin" value={biz.city} onChange={e => up('city', e.target.value)} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>State</FormLabel>
                                <Input placeholder="TX" value={biz.state} onChange={e => up('state', e.target.value)} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Zip Code</FormLabel>
                                <Input placeholder="78664" value={biz.zipCode} onChange={e => up('zipCode', e.target.value)} />
                            </FormControl>
                        </SimpleGrid>
                        <FormControl>
                            <Checkbox
                                isChecked={biz.hideHomeAddress}
                                onChange={e => up('hideHomeAddress', e.target.checked)}
                                colorScheme="blue"
                            >
                                Hide my street address (home-based business)
                            </Checkbox>
                            <Text fontSize="sm" color="gray.500" mt={1} ml={6}>
                                Check this if you run pickup and delivery from home with no public drop-off location.
                                Your website, booking pages, maps, and search listings will show only your city and
                                service area. We still keep the street privately for driver routing.
                            </Text>
                        </FormControl>
                        <Divider />
                        <Heading size="sm">Owner / Admin</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                                <FormLabel>First Name</FormLabel>
                                <Input value={biz.ownerFirstName} onChange={e => up('ownerFirstName', e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Last Name</FormLabel>
                                <Input value={biz.ownerLastName} onChange={e => up('ownerLastName', e.target.value)} />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel>Owner Phone (POS login)</FormLabel>
                                <Input placeholder="+1 512 555 0123" value={biz.ownerPhone} onChange={e => up('ownerPhone', e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Owner Email</FormLabel>
                                <Input type="email" placeholder="owner@example.com" value={biz.ownerEmail} onChange={e => up('ownerEmail', e.target.value)} />
                            </FormControl>
                        </SimpleGrid>
                    </VStack>
                )}

                {/* Step 2: Branding */}
                {activeStep === 1 && (
                    <VStack spacing={4} align="stretch">
                        <Heading size="md">Branding</Heading>
                        <Text fontSize="sm" color="gray.500">Optional — make it yours. You can change this anytime.</Text>
                        <FormControl>
                            <FormLabel fontWeight="bold">Business Logo</FormLabel>
                            <Input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                p={1}
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    if (file.size > 2 * 1024 * 1024) {
                                        toast({ title: 'Logo too large', description: 'Max 2MB', status: 'error', duration: 3000 });
                                        return;
                                    }
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                        setLogoPreview(reader.result);
                                        setLogoBase64(reader.result.split(',')[1]);
                                    };
                                    reader.readAsDataURL(file);
                                }}
                            />
                            {logoPreview && (
                                <Box mt={3} p={3} bg="gray.50" borderRadius="md" textAlign="center">
                                    <img src={logoPreview} alt="Logo preview" style={{ maxHeight: '80px', margin: '0 auto' }} />
                                    <Button size="xs" mt={2} variant="ghost" colorScheme="red" onClick={() => { setLogoPreview(''); setLogoBase64(''); }}>Remove</Button>
                                </Box>
                            )}
                        </FormControl>
                        <FormControl>
                            <FormLabel fontWeight="bold">Brand Color</FormLabel>
                            <SimpleGrid columns={{ base: 4, md: 8 }} spacing={3}>
                                {THEME_COLORS.map(color => (
                                    <Box
                                        key={color.value}
                                        w="100%" h="50px" bg={color.bg} borderRadius="md" cursor="pointer"
                                        border={themeColor === color.value ? '3px solid #1A202C' : '2px solid transparent'}
                                        onClick={() => setThemeColor(color.value)}
                                        display="flex" alignItems="center" justifyContent="center"
                                        transition="all 0.2s" _hover={{ transform: 'scale(1.05)' }}
                                    >
                                        <Text fontSize="xs" color="white" fontWeight="bold">{color.name}</Text>
                                    </Box>
                                ))}
                            </SimpleGrid>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontWeight="bold">Tagline / Slogan</FormLabel>
                            <Input placeholder="e.g. Fresh clothes, delivered to your door" value={tagline} onChange={e => setTagline(e.target.value)} />
                        </FormControl>
                    </VStack>
                )}

                {/* Step 3: Agreement */}
                {activeStep === 2 && (
                    <VStack spacing={4} align="stretch">
                        <Heading size="md">Service Agreement</Heading>
                        <Card variant="outline" bg="gray.50">
                            <CardBody>
                                <VStack spacing={3} align="stretch" fontSize="sm">
                                    <Heading size="sm">Platform Service Agreement</Heading>
                                    <Divider />
                                    <Text>A tiered monthly platform fee applies to managed hosting based on your monthly revenue:</Text>
                                    <Table size="sm" variant="simple">
                                        <Thead><Tr><Th>Monthly Revenue</Th><Th>Platform Fee</Th></Tr></Thead>
                                        <Tbody>
                                            <Tr><Td>$0 – $999</Td><Td fontWeight="bold" color="green.600">Free</Td></Tr>
                                            <Tr><Td>$1,000 – $4,999</Td><Td>$49/month</Td></Tr>
                                            <Tr><Td>$5,000 – $9,999</Td><Td>$74/month</Td></Tr>
                                            <Tr><Td>$10,000 – $14,999</Td><Td>$99/month</Td></Tr>
                                            <Tr><Td>$15,000 – $24,999</Td><Td>$124/month</Td></Tr>
                                            <Tr><Td>$25,000+</Td><Td fontWeight="bold">$149/month (cap)</Td></Tr>
                                        </Tbody>
                                    </Table>
                                    <Text fontSize="xs" color="gray.500">
                                        Max platform fee is <strong>$149/month</strong>. No setup fees. Optional usage-based
                                        add-ons (SMS, AI chat, AI item counting) are billed only when enabled and can be
                                        toggled anytime in the WDF POS. Cancel anytime; outstanding invoices remain due.
                                    </Text>
                                </VStack>
                            </CardBody>
                        </Card>
                        <Divider />
                        <FormControl isRequired>
                            <FormLabel fontWeight="bold">Electronic Signature</FormLabel>
                            <Text fontSize="sm" color="gray.600" mb={2}>Type your full legal name to sign.</Text>
                            <Input placeholder="Your full name (e.g. John Smith)" value={signatureName} onChange={e => setSignatureName(e.target.value)} fontStyle="italic" fontSize="lg" />
                        </FormControl>
                        <Text fontSize="xs" color="gray.500">Date: {signatureDate}</Text>
                        <Checkbox isChecked={agreementSigned} onChange={e => setAgreementSigned(e.target.checked)} colorScheme="blue" size="lg">
                            <Text fontSize="sm">
                                I agree to the Platform Service Agreement. Tiered platform fees apply based on monthly
                                revenue (max $149/month); optional usage-based add-ons are billed separately only when enabled.
                            </Text>
                        </Checkbox>
                    </VStack>
                )}

                {/* Step 4: Review */}
                {activeStep === 3 && (
                    <VStack spacing={4} align="stretch">
                        <Heading size="md">Review & Launch</Heading>
                        <Card variant="outline">
                            <CardBody>
                                <SimpleGrid columns={2} spacing={2}>
                                    <Text fontWeight="bold">Business:</Text><Text>{biz.laundryName}</Text>
                                    <Text fontWeight="bold">Address:</Text>
                                    <Text>
                                        {biz.hideHomeAddress
                                            ? `${biz.city}, ${biz.state} ${biz.zipCode} (street hidden from the public site)`
                                            : `${biz.street}, ${biz.city}, ${biz.state} ${biz.zipCode}`}
                                    </Text>
                                    <Text fontWeight="bold">Owner:</Text><Text>{[biz.ownerFirstName, biz.ownerLastName].filter(Boolean).join(' ') || '—'}</Text>
                                    <Text fontWeight="bold">Phone:</Text><Text>{biz.ownerPhone}</Text>
                                    <Text fontWeight="bold">Theme:</Text><Text><Badge colorScheme={themeColor}>{themeColor}</Badge></Text>
                                    <Text fontWeight="bold">Logo:</Text><Text>{logoBase64 ? '✅ Uploaded' : '📦 Default'}</Text>
                                </SimpleGrid>
                            </CardBody>
                        </Card>
                        <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            We'll set up a starter service menu and an all-week schedule automatically. Free to start — from $49/mo after you pass $1,000 in monthly revenue (max $149/mo).
                        </Alert>
                    </VStack>
                )}

                <Divider />

                <HStack justify="space-between">
                    <Button variant="ghost" onClick={() => setActiveStep(Math.max(0, activeStep - 1))} isDisabled={activeStep === 0 || isSubmitting}>
                        Back
                    </Button>
                    {activeStep < steps.length - 1 ? (
                        <Button colorScheme="blue" onClick={() => setActiveStep(activeStep + 1)} isDisabled={!canProceed()}>
                            Next
                        </Button>
                    ) : (
                        <Button colorScheme="green" onClick={handleSubmit} isLoading={isSubmitting} loadingText="Creating…" isDisabled={!agreementSigned || signatureName.trim().length <= 2}>
                            Launch My Laundry
                        </Button>
                    )}
                </HStack>
            </VStack>
              </CardBody>
            </Card>
        </Container>
        <SlbFooter />
        </Box>
    );
};

export default QuickStartPage;
